import { createAsyncThunk } from '@reduxjs/toolkit';

import { normalizeLanguageCode } from 'Consts/language';
import { LocalStorageKeys } from 'Consts/localStorageKeys';
import { connectSignalR } from 'SignalR';

import { usersApi } from '@/Api';
import type {
  ISaveAreTipsHiddenRequest,
  ISaveCurrentUserInfoRequest,
  ISaveIsMachineBarPinnedRequest,
  ISaveIsStartBannerHiddenRequest,
  ISaveLanguageCodeRequest,
  ISaveThemeRequest
} from '@/libs/models';
import { ResultHelpers } from '@/libs/models';

const saveThemeToLocalStorage = (theme: string): void =>
  localStorage.setItem(LocalStorageKeys.theme, theme);

export const getCurrentUserInfo = createAsyncThunk('users/getCurrentUserInfo', async () => {
  const result = await usersApi.getCurrentUser();
  return ResultHelpers.unwrapWith(
    result,
    async data => {
      saveThemeToLocalStorage(data.theme);
      connectSignalR();
      return data;
    },
    'Failed to get current user info'
  );
});

export const saveCurrentUserInfo = createAsyncThunk(
  'users/saveCurrentUserInfo',
  async (request: ISaveCurrentUserInfoRequest) => {
    await usersApi.saveCurrentUserInfo(request);
    return request;
  }
);

export const saveLanguage = createAsyncThunk(
  'users/saveLanguage',
  async ({ languageCode, isLogged }: { languageCode: string; isLogged: boolean }) => {
    const normalizedLanguageCode = normalizeLanguageCode(languageCode);

    if (isLogged) {
      await usersApi.saveLanguageCode({
        languageCode: normalizedLanguageCode
      } as ISaveLanguageCodeRequest);
    }

    return normalizedLanguageCode;
  }
);

export const saveTheme = createAsyncThunk(
  'users/saveTheme',
  async ({ theme, isLogged }: { theme: string; isLogged: boolean }) => {
    if (isLogged) {
      await usersApi.saveTheme({ theme } as ISaveThemeRequest);
    }

    return theme;
  }
);

export const syncPreferencesFromUserData = createAsyncThunk(
  'users/syncPreferencesFromUserData',
  async (userData: {
    theme?: string;
    languageCode?: string;
    timezone?: string;
    isStartBannerHidden?: boolean;
  }) => {
    let normalizedLanguage = userData.languageCode;

    if (userData.languageCode) {
      normalizedLanguage = normalizeLanguageCode(userData.languageCode);
    }

    return {
      theme: userData.theme,
      language: normalizedLanguage,
      timezone: userData.timezone,
      isStartBannerHidden: userData.isStartBannerHidden
    };
  }
);

export const saveIsStartBannerHidden = createAsyncThunk(
  'users/saveIsStartBannerHidden',
  async ({
    isStartBannerHidden,
    isLogged
  }: {
    isStartBannerHidden: boolean;
    isLogged: boolean;
  }) => {
    if (isLogged) {
      await usersApi.saveIsStartBannerHidden({
        isStartBannerHidden
      } as ISaveIsStartBannerHiddenRequest);
    }

    return isStartBannerHidden;
  }
);

export const saveIsMachineBarPinned = createAsyncThunk(
  'users/saveIsMachineBarPinned',
  async ({ isMachineBarPinned, isLogged }: { isMachineBarPinned: boolean; isLogged: boolean }) => {
    if (isLogged) {
      await usersApi.saveIsMachineBarPinned({
        isMachineBarPinned
      } as ISaveIsMachineBarPinnedRequest);
    }

    return isMachineBarPinned;
  }
);

export const saveAreTipsHidden = createAsyncThunk(
  'users/saveAreTipsHidden',
  async ({ areTipsHidden, isLogged }: { areTipsHidden: boolean; isLogged: boolean }) => {
    if (isLogged) {
      await usersApi.saveAreTipsHidden({
        areTipsHidden
      } as ISaveAreTipsHiddenRequest);
    }

    return areTipsHidden;
  }
);
